import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Trash2, Link2, Edit3, Award, Database, CloudSun, FileText,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import AddSiteDialog from "./analytics/AddSiteDialog";
import PortfolioBackfillDialog from "./analytics/PortfolioBackfillDialog";
import PortfolioWeatherBackfillDialog from "./analytics/PortfolioWeatherBackfillDialog";
import SiteDetail from "./analytics/SiteDetail";
import ClassInsightsCard from "./analytics/ClassInsightsCard";
import CustomReports from "./analytics/CustomReports";
import PortfolioOrkestra, { OrkToolbar, exportPortfolioCsv } from "./analytics/PortfolioOrkestra";
import "./analytics/orkestra.css";
import SyncHealthBadge from "../components/SyncHealthBadge";
import { CLASS_KEYS, CLASS_BY_KEY, resolveClass } from "../lib/classification";
import DateInput from "../components/DateInput";

const RANGES = [
  { key: "24h", label: "24 h" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "365d", label: "1 year" },
  { key: "custom", label: "Custom" },
];

// ISO date (YYYY-MM-DD) helper — used to default the Custom range inputs
// to the last 30 days on first click.
const _iso = (d) => d.toISOString().slice(0, 10);

// LocalStorage keys for persisting the user's last-used range across
// browser tabs / sessions. Small quality-of-life win: come back Monday
// morning and land on the same view you left Friday evening.
const _LS_RANGE_KEY  = "solaros.analytics.range";
const _LS_START_KEY  = "solaros.analytics.customStart";
const _LS_END_KEY    = "solaros.analytics.customEnd";
const _VALID_RANGES  = new Set(["24h", "7d", "30d", "90d", "365d", "custom"]);
function _lsGetRange() {
  try {
    const v = window.localStorage.getItem(_LS_RANGE_KEY);
    return _VALID_RANGES.has(v) ? v : "7d";
  } catch { return "7d"; }
}
function _lsGetDate(key, fallback) {
  try {
    const v = window.localStorage.getItem(key);
    return /^\d{4}-\d{2}-\d{2}$/.test(v || "") ? v : fallback;
  } catch { return fallback; }
}
function _lsSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
}

// Common reporting-window shortcuts inside Custom mode. All bounds are
// UTC-anchored to keep the API filter deterministic; fiscal year uses the
// UK convention (April → March).
function _customPresets() {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();       // 0-based
  const startOfMonth = new Date(Date.UTC(y, m, 1));
  const startOfPrevMonth = new Date(Date.UTC(y, m - 1, 1));
  const endOfPrevMonth = new Date(Date.UTC(y, m, 0));
  const startOfYear = new Date(Date.UTC(y, 0, 1));
  // UK fiscal year — currently in-progress if today ≥ Apr 1, else last one
  const fyStartYear = m >= 3 ? y : y - 1;
  const fyStart = new Date(Date.UTC(fyStartYear, 3, 1));
  const fyEnd = new Date(Date.UTC(fyStartYear + 1, 2, 31));
  // Quarters — current calendar year
  const qStart = (q) => new Date(Date.UTC(y, (q - 1) * 3, 1));
  const qEnd = (q) => new Date(Date.UTC(y, q * 3, 0));
  return [
    { key: "this-month",  label: "This month",  start: startOfMonth,       end: now },
    { key: "last-month",  label: "Last month",  start: startOfPrevMonth,   end: endOfPrevMonth },
    { key: "ytd",         label: "YTD",         start: startOfYear,        end: now },
    { key: "q1",          label: "Q1",          start: qStart(1),          end: qEnd(1) },
    { key: "q2",          label: "Q2",          start: qStart(2),          end: qEnd(2) },
    { key: "q3",          label: "Q3",          start: qStart(3),          end: qEnd(3) },
    { key: "q4",          label: "Q4",          start: qStart(4),          end: qEnd(4) },
    { key: "fiscal-year", label: "Fiscal year", start: fyStart,            end: fyEnd },
  ];
}

export default function Analytics() {
  const initialSite = new URLSearchParams(window.location.search).get("site");
  const [tab, setTab] = useState(initialSite ? "site" : "portfolio");   // portfolio | site | manage | reports
  const [selectedSiteId, setSelectedSiteId] = useState(initialSite);
  // Class filter — driven by the "Filter page" button on <ClassInsightsCard>.
  // Persists via URL query param `?filter=retro|development` so filtered
  // views are bookmarkable and back/forward navigation restores the state.
  const [pageFilter, setPageFilterState] = useState(null);
  useEffect(() => {
    // One-shot: read the URL param on mount and set state. Not synced back
    // to the URL when clicked — the URL is updated inside setPageFilter.
    const p = new URLSearchParams(window.location.search).get("filter");
    if (CLASS_KEYS.includes(p)) setPageFilterState(p);
  }, []);
  const setPageFilter = (cls) => {
    setPageFilterState(cls);
    const url = new URL(window.location.href);
    if (cls) url.searchParams.set("filter", cls);
    else url.searchParams.delete("filter");
    window.history.pushState({}, "", url);
  };
  // Range + custom bounds are persisted in localStorage so users land on
  // the same view they were last studying when they reopen the page.
  const [range, setRange] = useState(_lsGetRange);
  const [customStart, setCustomStart] = useState(() => _lsGetDate(_LS_START_KEY, (() => {
    const d = new Date(); d.setUTCDate(d.getUTCDate() - 30); return _iso(d);
  })()));
  const [customEnd, setCustomEnd] = useState(() => _lsGetDate(_LS_END_KEY, _iso(new Date())));
  const [projects, setProjects] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [regoBusy, setRegoBusy] = useState(false);
  const [showBackfill, setShowBackfill] = useState(false);
  const [showWeatherBackfill, setShowWeatherBackfill] = useState(false);

  const downloadRegoPack = async () => {
    setRegoBusy(true);
    try {
      const year = new Date().getUTCFullYear();
      const res = await api.get(
        `/monitoring/portfolio/rego-submission.pdf?year=${year}`,
        { responseType: "blob" },
      );
      const filename = (res.headers["content-disposition"] || "")
        .match(/filename="?([^"]+)"?/)?.[1] || `REGO_submission_${year}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = filename;
      document.body.appendChild(dl);
      dl.click();
      setTimeout(() => { URL.revokeObjectURL(dl.href); dl.remove(); }, 200);
      toast.success(`REGO pack for ${year} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "REGO pack export failed");
    } finally { setRegoBusy(false); }
  };
  const [editSite, setEditSite] = useState(null);
  const [view, setView] = useState("chart");
  const [packBusy, setPackBusy] = useState(false);
  const rangeQuery = range === "custom" ? `range=custom&start_date=${customStart}&end_date=${customEnd}` : `range=${range}`;
  const downloadReportPack = async () => {
    setPackBusy(true);
    try {
      const res = await api.get(`/analytics/portfolio-report.pdf?${rangeQuery}`, { responseType: "blob" });
      const filename = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "eneprox_portfolio_report.pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      a.download = filename;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 200);
      toast.success("Report pack downloaded");
    } catch { toast.error("Report pack failed"); } finally { setPackBusy(false); }
  };

  const loadProjects = async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
  };
  const loadPortfolio = async () => {
    // Custom range needs both bounds; presets are anchored to `now` on the backend.
    let url = `/analytics/portfolio-15min?range=${range}`;
    if (range === "custom") {
      if (!customStart || !customEnd) return;
      if (customEnd < customStart) {
        toast.error("End date must be on or after start date");
        return;
      }
      url += `&start_date=${customStart}&end_date=${customEnd}`;
    }
    try {
      const { data } = await api.get(url);
      setPortfolio(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load portfolio production");
    }
  };
  useEffect(() => { loadProjects(); }, []);
  // Persist last-used range + custom bounds. Loading is fired here so
  // presets refetch immediately AND a Custom range with cached bounds
  // (from a previous session) fires on mount too — user lands on the
  // same view they left with.
  useEffect(() => {
    _lsSet(_LS_RANGE_KEY, range);
    loadPortfolio();
  }, [range, projects.length]);
  // Persist custom-range bounds separately — they change without the
  // range key changing (e.g. clicking a Custom preset like YTD).
  useEffect(() => {
    if (range === "custom") {
      _lsSet(_LS_START_KEY, customStart);
      _lsSet(_LS_END_KEY, customEnd);
    }
  }, [range, customStart, customEnd]);

  const syncAll = async () => {
    setBusy(true);
    let ok = 0;
    for (const p of projects.filter(x => x.solaredge_site_id && x.solaredge_api_key)) {
      try { await api.post(`/analytics/sites/${p.id}/sync-15min?hours=24`); ok += 1; }
      catch { /* noop — reported in the badge */ }
    }
    toast.success(`Synced ${ok} site${ok === 1 ? "" : "s"}`);
    await loadPortfolio();
    setBusy(false);
  };

  const deleteSite = async (pid, name) => {
    if (!window.confirm(`Delete "${name}" and all its data? This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${pid}`);
      toast.success("Site deleted");
      await loadProjects(); await loadPortfolio();
      if (selectedSiteId === pid) { setSelectedSiteId(null); setTab("portfolio"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sites = useMemo(() => {
    // Unclassified projects normalise to DEFAULT_CLASS via `resolveClass`
    // so nothing gets orphaned when the leadership filter is applied.
    const matchesFilter = (p) => {
      if (!pageFilter) return true;
      return resolveClass(p.business_classification) === pageFilter;
    };
    return projects.filter(matchesFilter).map(p => {
      const bySite = (portfolio?.per_site || []).find(s => s.project_id === p.id);
      return { ...p, ...(bySite || {}) };
    });
  }, [projects, portfolio, pageFilter]);

  return (
    <div className="ork-page" data-testid="analytics-page">
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="ork-eyebrow">Reporting &amp; analysis</div>
          <h1 className="ork-h1">Portfolio production</h1>
          <p className="ork-lede">Track energy performance at scale — actual generation benchmarked against weather-based potential, synced daily from SolarEdge or MeterOnline.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={syncAll} disabled={busy} className="btn-ghost" data-testid="sync-all-btn">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Sync all
          </button>
          <button onClick={() => setShowBackfill(true)} className="btn-ghost"
                  data-testid="portfolio-backfill-btn"
                  title="Portfolio-wide historic backfill — pull HH data from every configured meter for the last N days">
            <Database size={13} /> Backfill all
          </button>
          <button onClick={() => setShowWeatherBackfill(true)} className="btn-ghost"
                  data-testid="portfolio-weather-backfill-btn"
                  title="Portfolio-wide historic weather backfill — pull hourly Open-Meteo GHI for every site with lat/lng">
            <CloudSun size={13} /> Backfill weather
          </button>
          <button onClick={downloadRegoPack} disabled={regoBusy} className="btn-ghost"
                  data-testid="rego-pack-btn"
                  title="Portfolio-wide REGO submission pack for the current calendar year — hand it to Ofgem or your REGO broker">
            <Award size={13} /> {regoBusy ? "Building…" : "REGO pack"}
          </button>
          <button onClick={downloadReportPack} disabled={packBusy} className="btn-primary" data-testid="report-pack-btn"
                  title="eneprox-branded PDF report pack for investors — KPIs, revenue vs forecast, generation chart and per-site table for the selected range">
            <FileText size={13} /> {packBusy ? "Building…" : "Report pack"}
          </button>
          <button onClick={() => setShowAdd(true)} className="btn-accent" data-testid="add-site-btn">
            <Plus size={14} /> Add site
          </button>
        </div>
      </div>

      {range === "custom" && (
        <div className="ork-panel !py-3 !px-4 mb-6 flex items-center gap-3 flex-wrap"
             data-testid="custom-range-controls">
          {/* Preset shortcuts — one click to jump to common reporting
              windows without picking dates manually. Clicking a preset
              also fires the request immediately (no separate Apply). */}
          <div className="flex items-center gap-1 flex-wrap"
               data-testid="custom-range-presets">
            {_customPresets().map(p => (
              <button key={p.key} type="button"
                      onClick={async () => {
                        const s = _iso(p.start), e = _iso(p.end);
                        setCustomStart(s); setCustomEnd(e);
                        // Use the freshly-picked dates instead of the
                        // just-set React state (which won't be visible
                        // to loadPortfolio in this same tick).
                        const url = `/analytics/portfolio-15min?range=custom&start_date=${s}&end_date=${e}`;
                        try {
                          const { data } = await api.get(url);
                          setPortfolio(data);
                        } catch (err) {
                          toast.error(formatApiError(err.response?.data?.detail) || "Could not load range");
                        }
                      }}
                      data-testid={`custom-preset-${p.key}`}
                      className="px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-md border border-[var(--line)] bg-white hover:border-[var(--accent)] hover:bg-[var(--surface-2)] transition-colors">
                {p.label}
              </button>
            ))}
          </div>
          {/* Manual date pickers — for windows that don't fit any preset. */}
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            <DateInput value={customStart}
                   onChange={(e) => setCustomStart(e.target.value)}
                   max={customEnd}
                   className="input-box !w-auto !py-1.5 text-sm border border-[var(--line)] bg-white"
                   data-testid="custom-range-start" />
            <span className="text-sm" style={{ color: "var(--ink-3)" }}>→</span>
            <DateInput value={customEnd}
                   onChange={(e) => setCustomEnd(e.target.value)}
                   min={customStart}
                   max={_iso(new Date())}
                   className="input-box !w-auto !py-1.5 text-sm border border-[var(--line)] bg-white"
                   data-testid="custom-range-end" />
            <button onClick={loadPortfolio}
                    className="btn-accent"
                    data-testid="custom-range-apply">
              Apply
            </button>
          </div>
        </div>
      )}
      {/* Tab strip */}
      <div className="ork-tabs">
        {[
          { key: "portfolio", label: "Portfolio" },
          { key: "site", label: selectedSiteId ? `Site · ${sites.find(s => s.id === selectedSiteId)?.name || "…"}` : "Per site", disabled: !selectedSiteId },
          { key: "manage", label: `Sites (${projects.length})` },
          { key: "reports", label: "Custom Reports" },
        ].map(t => (
          <button key={t.key} onClick={() => !t.disabled && setTab(t.key)}
                  disabled={t.disabled}
                  data-testid={`analytics-tab-${t.key}`}
                  className={`ork-tab ${tab === t.key ? "is-on" : ""}`}>
            {t.label}
          </button>
        ))}
      </div>

      {pageFilter && (() => {
        const meta = CLASS_BY_KEY[pageFilter];
        if (!meta) return null;
        return (
          <div className="mb-4 p-3 rounded-lg flex items-center justify-between gap-3"
               style={{ background: `${meta.tone}1F`, border: `1px solid ${meta.tone}55` }}
               data-testid="analytics-page-filter-banner">
            <div className="text-xs">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold mr-2"
                    style={{ color: meta.tone }}>
                Filtered
              </span>
              <span>Showing <strong>{meta.label}</strong> sites only across the whole Analytics page.</span>
            </div>
            <button
              type="button"
              onClick={() => setPageFilter(null)}
              className="text-[11px] font-semibold underline decoration-dotted underline-offset-2 hover:no-underline"
              data-testid="analytics-page-filter-clear"
            >
              Clear filter
            </button>
          </div>
        );
      })()}

      {tab === "portfolio" && (
        <>
          <PortfolioOrkestra portfolio={portfolio} sites={sites} view={view} totalSites={sites.length} rangeQuery={rangeQuery}
                         before={<SyncHealthBadge onSelectSite={(id) => { setSelectedSiteId(id); setTab("site"); }} />}
                         onSelectSite={(id) => { setSelectedSiteId(id); setTab("site"); }}
                         onEdit={(p) => setEditSite(p)} />
          <div className="mt-8"><ClassInsightsCard pageFilter={pageFilter} onSetPageFilter={setPageFilter} /></div>
        </>
      )}
      {tab === "site" && selectedSiteId && (
        <SiteDetail projectId={selectedSiteId} range={range} rangeQuery={rangeQuery}
                    onEdit={() => { const p = projects.find(x => x.id === selectedSiteId); if (p) setEditSite(p); }} />
      )}
      {tab === "manage" && (
        <ManageSites sites={sites}
                     onSelect={(id) => { setSelectedSiteId(id); setTab("site"); }}
                     onEdit={(p) => setEditSite(p)}
                     onDelete={deleteSite} />
      )}
      {tab === "reports" && (
        <CustomReports />
      )}

      {tab !== "reports" && tab !== "manage" && (
        <OrkToolbar ranges={RANGES} range={range} setRange={setRange}
          count={tab === "portfolio" ? sites.length : null}
          view={view} setView={tab === "portfolio" ? setView : null}
          onExport={tab === "portfolio" && portfolio ? () => exportPortfolioCsv(portfolio, sites) : null} />
      )}

      {showAdd && (
        <AddSiteDialog onClose={() => setShowAdd(false)}
                       onSaved={async () => { setShowAdd(false); await loadProjects(); }} />
      )}
      {editSite && (
        <AddSiteDialog site={editSite} onClose={() => setEditSite(null)}
                       onSaved={async () => { setEditSite(null); await loadProjects(); await loadPortfolio(); }} />
      )}
      {showBackfill && (
        <PortfolioBackfillDialog onClose={() => {
          setShowBackfill(false);
          loadPortfolio();   // refresh chart data in case new samples landed
        }} />
      )}
      {showWeatherBackfill && (
        <PortfolioWeatherBackfillDialog onClose={() => {
          setShowWeatherBackfill(false);
          loadPortfolio();   // refresh in case monitoring rows were re-scored
        }} />
      )}
    </div>
    </div>
  );
}

/* ---------- Sites management (CRUD list) ---------- */
function ManageSites({ sites, onSelect, onEdit, onDelete }) {
  return (
    <section className="swiss-card-flat overflow-hidden" data-testid="manage-sites-table">
      <table className="data-table">
        <thead>
          <tr>
            <th>Site</th>
            <th>Postcode</th>
            <th>Coordinates</th>
            <th>SolarEdge</th>
            <th>PR</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sites.map(p => (
            <tr key={p.id} data-testid={`site-row-${p.id}`}>
              <td>
                <button onClick={() => onSelect(p.id)} className="text-left font-medium hover:text-[var(--accent)]"
                        data-testid={`site-view-${p.id}`}>
                  {p.name}
                </button>
                <div className="muted text-xs">{p.customer_name}</div>
              </td>
              <td className="font-mono text-xs">{p.postcode || "—"}</td>
              <td className="font-mono text-xs">
                {p.latitude != null && p.longitude != null
                  ? `${Number(p.latitude).toFixed(4)}, ${Number(p.longitude).toFixed(4)}`
                  : <span className="muted">Not set</span>}
              </td>
              <td className="text-xs">
                {p.solaredge_site_id ? (
                  <span className="tag ok"><Link2 size={10} /> {p.solaredge_site_id}</span>
                ) : (
                  <span className="tag warn">Manual only</span>
                )}
              </td>
              <td className="font-mono text-xs">{p.performance_ratio ?? "0.80"}</td>
              <td className="num">
                <button onClick={() => onEdit(p)} className="p-1.5 hover:bg-zinc-100 rounded-sm mr-1"
                        data-testid={`site-edit-${p.id}`} title="Edit">
                  <Edit3 size={13} />
                </button>
                <button onClick={() => onDelete(p.id, p.name)} className="p-1.5 hover:bg-red-50 rounded-sm text-red-600"
                        data-testid={`site-delete-${p.id}`} title="Delete">
                  <Trash2 size={13} />
                </button>
              </td>
            </tr>
          ))}
          {!sites.length && (
            <tr><td colSpan={6} className="muted text-center py-8">No sites yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
